import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { authApi } from './auth/authApi';
import { authStore, type DashboardAuth } from './auth/authStore';
import { CrossLaunchView } from './auth/CrossLaunchView';
import { LoginView } from './auth/LoginView';
import { DashboardShell } from './dashboard/DashboardShell';
import { BrokerageCanvasserDirectoryProvider } from './dashboard/BrokerageCanvasserDirectoryContext';
import { AgentHomePage } from './dashboard/pages/AgentHomePage';
import { AboutPage } from './dashboard/pages/AboutPage';
import { DashboardPage } from './dashboard/pages/DashboardPage';
import { DoorVisitDetailPage } from './dashboard/pages/DoorVisitDetailPage';
import { DoorVisitsPage } from './dashboard/pages/DoorVisitsPage';
import { DoorVisitReportRunsPage } from './dashboard/door-visit-reports/DoorVisitReportRunsPage';
import { OnboardCanvassersPage } from './dashboard/pages/OnboardCanvassersPage';
import { CanvassingSessionDetailPage } from './dashboard/canvassing-sessions/CanvassingSessionDetailPage';
import { CanvassingSessionsPage } from './dashboard/canvassing-sessions/CanvassingSessionsPage';
import { CoreConfigurationPage } from './dashboard/core-configuration/CoreConfigurationPage';
import { SeedDataPage } from './dashboard/seed-data/SeedDataPage';
import { VoterDetailPage } from './dashboard/voters/VoterDetailPage';
import { VotersPage } from './dashboard/voters/VotersPage';
import { resolveClientEnv } from './graphql/config/resolveClientEnv';
import { LegalDocumentPage, type LegalDocumentPresentation } from './legal/LegalDocumentPage';
import { legalDocuments } from './legal/legalDocuments';
import { OnboardUserPage } from './onboarding/OnboardUserPage';

const legalDocumentRoutes = (presentation: LegalDocumentPresentation) =>
  legalDocuments.map(document => (
    <Route
      key={document.path}
      path={document.path}
      element={<LegalDocumentPage document={document} presentation={presentation} />}
    />
  ));

type BrokerOnlyRouteProps = {
  role: DashboardAuth['role'];
  children: ReactNode;
};

const BrokerOnlyRoute = ({ role, children }: BrokerOnlyRouteProps) =>
  role === 'broker' ? children : <Navigate to="/" replace />;

type BrokerageCanvasserDirectoryRouteProps = {
  auth: DashboardAuth;
};

const BrokerageCanvasserDirectoryRoute = ({ auth }: BrokerageCanvasserDirectoryRouteProps) => (
  <BrokerOnlyRoute role={auth.role}>
    <BrokerageCanvasserDirectoryProvider
      key={JSON.stringify([auth.token, auth.agentId, auth.brokerageId, auth.role])}
      brokerageId={auth.brokerageId}
    >
      <Outlet />
    </BrokerageCanvasserDirectoryProvider>
  </BrokerOnlyRoute>
);

export const App = () => {
  const [auth, setAuth] = useState<DashboardAuth | null>(() => authStore.getAuth());

  useEffect(() => authStore.subscribe(setAuth), []);

  return (
    <Routes>
      <Route path="/onboard-user" element={<OnboardUserPage />} />
      <Route path="/cross-launch" element={<CrossLaunchView />} />
      {auth ? (
        <Route element={<DashboardShell auth={auth} />}>
          <Route element={auth.role === 'broker' ? <BrokerageCanvasserDirectoryRoute auth={auth} /> : <Outlet />}>
            {legalDocumentRoutes('dashboard')}
            <Route path="about" element={<AboutPage />} />
            <Route index element={auth.role === 'agent' ? <AgentHomePage /> : <DashboardPage />} />
            <Route
              path="door-visits"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <DoorVisitsPage />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="door-visits/report-runs"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <DoorVisitReportRunsPage />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="door-visits/:id"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <DoorVisitDetailPage />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="voters"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <VotersPage />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="voters/:id"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <VoterDetailPage />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="canvassing-sessions"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <CanvassingSessionsPage />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="canvassing-sessions/:sessionId"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <CanvassingSessionDetailPage />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="onboard-canvassers"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <OnboardCanvassersPage auth={auth} />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="seed-data"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <SeedDataPage />
                </BrokerOnlyRoute>
              }
            />
            <Route
              path="core-configuration"
              element={
                <BrokerOnlyRoute role={auth.role}>
                  <CoreConfigurationPage />
                </BrokerOnlyRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      ) : (
        <>
          {legalDocumentRoutes('public')}
          <Route
            path="*"
            element={
              <LoginView
                login={credentials => authApi.login(credentials, { env: resolveClientEnv() })}
                onSuccess={nextAuth => authStore.setAuth(nextAuth)}
              />
            }
          />
        </>
      )}
    </Routes>
  );
};
